import { useAutoneTranslation } from '@autone/translations';
import { cva } from 'class-variance-authority';
import { Square } from 'lucide-react';
import {
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Button } from '../../atoms/Button';
import { Textarea } from '../../atoms/Textarea';
import { ChatIcon, SendIcon } from '../../icons';
import { cn } from '../../utils';

type Variant = 'palette' | 'panel';

const MAX_HEIGHT_PX = 160;

const composerVariants = cva(
  cn(
    'relative overflow-hidden rounded-2xl border bg-card transition-all duration-200',
    'before:pointer-events-none before:absolute before:inset-0 before:rounded-2xl before:opacity-0 before:transition-opacity before:duration-200',
    'before:bg-[radial-gradient(120%_60%_at_50%_0%,theme(colors.primary/8%),transparent_70%)]',
  ),
  {
    variants: {
      variant: {
        palette: 'border-border shadow-2xl shadow-black/30',
        panel: 'border-border/60 shadow-md shadow-black/[0.04]',
      },
      active: {
        true: 'before:opacity-100',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'palette',
        active: true,
        class:
          'border-border shadow-2xl shadow-black/40 ring-1 ring-primary/20',
      },
      {
        variant: 'panel',
        active: true,
        class:
          'border-border shadow-lg shadow-primary/5 ring-1 ring-primary/10',
      },
    ],
    defaultVariants: {
      variant: 'panel',
      active: false,
    },
  },
);

interface ChatComposerProps {
  variant: Variant;
  isStreaming: boolean;
  onSend: (prompt: string) => void;
  onCancel: () => void;
  autoFocus?: boolean;
}

export const ChatComposer = forwardRef(function ChatComposer(
  { variant, isStreaming, onSend, onCancel, autoFocus }: ChatComposerProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  const { common } = useAutoneTranslation();
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const localRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      localRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  useEffect(() => {
    const el = localRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [draft]);

  const submit = useCallback(() => {
    const value = draft.trim();
    if (!value || isStreaming) return;
    onSend(value);
    setDraft('');
  }, [draft, isStreaming, onSend]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const active = focused || draft.length > 0 || isStreaming;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className={composerVariants({ variant, active })}
    >
      <div className="relative px-4 pt-3 pb-1">
        <Textarea
          ref={setRefs}
          autoFocus={autoFocus}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={common('chat.input-placeholder')}
          aria-label={common('chat.input-aria')}
          style={{ maxHeight: MAX_HEIGHT_PX }}
          className={cn(
            'w-full resize-none border-0 bg-transparent p-0 text-sm leading-relaxed shadow-none',
            'placeholder:text-muted-foreground',
            'focus-visible:border-0 focus-visible:ring-0',
            'min-h-[44px] overflow-y-auto',
          )}
        />
      </div>

      <div
        className={cn(
          'flex items-center justify-between gap-2 px-3 pb-2 pt-1',
          'transition-opacity duration-200',
          active ? 'opacity-100' : 'opacity-80',
        )}
      >
        <div className="flex items-center gap-1.5 pl-1 text-xs text-muted-foreground">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ChatIcon className="h-3 w-3" />
          </span>
          <span className="font-medium tracking-tight">
            {common('chat.title')}
          </span>
          <span className="hidden text-muted-foreground/60 sm:inline">·</span>
        </div>
        {isStreaming ? (
          <Button
            id={`agent-chat-stop-${variant}`}
            aria-label={common('chat.stop')}
            variant="secondary"
            size="icon-sm"
            type="button"
            onClick={onCancel}
            className="h-8 w-8 shrink-0 rounded-full"
          >
            <Square
              className="h-3.5 w-3.5"
              fill="currentColor"
              strokeWidth={0}
            />
          </Button>
        ) : (
          <Button
            id={`agent-chat-send-${variant}`}
            aria-label={common('chat.send')}
            variant="default"
            size="icon-sm"
            type="submit"
            disabled={!draft.trim()}
            className={cn(
              'h-8 w-8 shrink-0 rounded-full transition-all',
              draft.trim() ? 'shadow-md shadow-primary/20' : 'opacity-50',
            )}
          >
            <SendIcon className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </form>
  );
});
